'use client'

import useAddQueryToNewLink from 'app/hooks/useAddQueryToNewLink'
import { cn } from 'app/lib/utils'
import NextLink from 'next/link'
import { useParams, usePathname } from 'next/navigation'
import { ReactNode, useMemo } from 'react'

const Link = ({
    children,
    href,
    className,
    disableCondition,
    addQueryParams = false,
    style,
    target,
    activeClassName
}: {
    children: ReactNode
    href: string
    className?: string
    disableCondition?: boolean
    addQueryParams?: boolean
    style?: React.CSSProperties
    target?: string
    activeClassName?: string
}) => {
    const query = useParams()
    const pathname = usePathname()
    const { addQueryToNewLink } = useAddQueryToNewLink()

    // Memoize and validate href to prevent negative timeout issues
    const finalHref = useMemo(() => {
        if (!href || typeof href !== 'string') return '/'
        
        // Handle external URLs and special protocols (mailto, tel, etc.) - return as-is
        if (href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('#')) {
            return href
        }

        try {
            // Extract base path and query string from href
            const [basePath, queryString] = href.split('?')
            const lang = query?.lang || 'en'
            const baseHref = `/${lang}${basePath || ''}`

            // If href already has query params, preserve them
            if (queryString) {
                return `${baseHref}?${queryString}`
            }

            // Only add query params from current URL if specifically requested
            if (!addQueryParams) return baseHref
            
            const result = addQueryToNewLink(baseHref)
            // Ensure result is a valid string
            return result && typeof result === 'string' ? result : baseHref
        } catch (error) {
            console.error('[Link] Error generating href:', error)
            // Fallback to a safe default
            const lang = query?.lang || 'en'
            return `/${lang}${href}`
        }
    }, [href, query?.lang, addQueryParams, addQueryToNewLink])

    const isActive = activeClassName && pathname.includes(href)
    const finalClassName = cn(className, isActive && activeClassName, disableCondition && 'disabled')
    const finalTarget = href.startsWith('http') ? '_blank' : target

    return (
        <NextLink
            className={finalClassName}
            style={{
                pointerEvents: disableCondition ? 'none' : 'auto',
                ...style
            }}
            href={finalHref}
            target={finalTarget}
            prefetch={false}
        >
            {children}
        </NextLink>
    )
}
export default Link
